'use client'

import { useTranslations } from 'next-intl'
import { useEffect, useRef } from 'react'
import { toast } from 'sonner'
import { useHyperliquidFills, useHyperliquidPositions } from './useHyperliquidTrading'

/**
 * Watches positions + fills and fires a settlement toast when an outcome
 * position transitions from non-zero size to zero with a realized PnL
 * (i.e. validators resolved the binary and credited the winning side).
 *
 * Mount once globally (portfolio page or layout) so the toast fires even
 * when the user isn't on the market detail page.
 */
export function useHyperliquidSettlements() {
	const t = useTranslations('hyperliquid.toast')
	const { data: positions } = useHyperliquidPositions()
	const { data: fills } = useHyperliquidFills()

	// Track size-per-coin from the last poll so we can detect zero-transition.
	const prevSizesRef = useRef<Record<string, number>>({})
	// Per-coin "last seen fill time" — we only attribute fills NEWER than this
	// to a fresh settlement. Without this guard, a user who manually closed a
	// position weeks ago would see the old close-fill replayed as a fake
	// settlement toast the next time we observe a zero transition for the
	// same coin.
	const lastFillTimeRef = useRef<Record<string, number>>({})
	// Toast dedup — sonner already dedups by id, but we also avoid recomputing
	// the matching fill for the same (coin, fillTid) pair across renders.
	const toastedFillsRef = useRef<Set<number>>(new Set())

	useEffect(() => {
		if (!positions) return
		const prev = prevSizesRef.current
		const next: Record<string, number> = {}

		for (const p of positions) {
			next[p.position.coin] = Math.abs(Number.parseFloat(p.position.szi) || 0)
		}

		// Find coins that WERE non-zero last poll but ARE zero now (or missing)
		for (const [coin, prevSize] of Object.entries(prev)) {
			const currentSize = next[coin] ?? 0
			if (prevSize > 0 && currentSize === 0) {
				// Pick the LATEST settlement-shaped fill for this coin (closedPnl
				// non-zero) — fills come back roughly time-descending from the
				// HL API but we don't trust that, sort explicitly.
				const lastSeenTime = lastFillTimeRef.current[coin] ?? 0
				const candidates = (fills ?? [])
					.filter(
						f =>
							f.coin === coin &&
							f.time > lastSeenTime &&
							Math.abs(Number.parseFloat(f.closedPnl)) > 0.001
					)
					.sort((a, b) => b.time - a.time)
				const matchingFill = candidates[0]
				if (matchingFill && !toastedFillsRef.current.has(matchingFill.tid)) {
					const pnl = Number.parseFloat(matchingFill.closedPnl)
					const params = { pnl: Math.abs(pnl).toFixed(2), coin }
					toast.success(pnl >= 0 ? t('settledWin', params) : t('settledLoss', params), {
						id: `hl-settled-${coin}-${matchingFill.tid}`,
						duration: 8000,
					})
					toastedFillsRef.current.add(matchingFill.tid)
					lastFillTimeRef.current[coin] = matchingFill.time
				}
			}
		}

		prevSizesRef.current = next
	}, [positions, fills])
}
